/**
 * @file PC 端首页组件
 */


import {Component} from 'san';
import './index.less';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import Button from '@baidu/cosmic/button';
import Icon from '@baidu/cosmic/icon';
import LottieMouse from '../lottie/mouse';
import LeftStar from '../lottie/left-star';
import RightStar from '../lottie/right-star';
import DownloadBubble from '../download-bubble/index';
import Footer from '../footer/index';
import VideoPlyr from '../video-plyr/index';
import {
    LOGO_URL_WHITE,
    LOGO_URL_BLACK,
    QR_CODE_ANDROID_URL,
    QR_CODE_IOS_URL,
    POSTER_URL,
    VIDEO_URL,
    PAGE_TITLE_1,
    PAGE_TITLE_2,
    PAGE_SUBTITLE,
    SLOGAN,
    DOWNLOAD_ICON_URL,
    PC_BG_DARK,
    PC_BG_LIGHT,
    PRODUCT_BG_1,
    PRODUCT_BG_2,
    PRODUCT_BG_3,
    PRODUCT_BG_4,
} from '../../../../common/const/index';
import {handleObserver, handleLog} from '../../../../utils/index';

export default class App extends Component {
    static template = /* html */`
        <div>
            <div class="pc-container">
                <div class="header-wrap">
                    <img s-show="isFirstSection" class="site-logo" src="{{logoWhite}}" alt="心响"/>
                    <img s-show="!isFirstSection" class="site-logo" src="{{logoBlack}}" alt="心响"/>
                    <div
                        class="
                            download-wrap
                            {{isFirstSection ? 'download-wrap-disabled' : ''}}
                        "
                    >
                        <div class="download-wrap-btn" on-mouseenter="native:handleMouseEnter('all')">
                            <span class="download-wrap-btn-icon"></span>
                            <span>下载心响APP</span>
                        </div>
                        
                        <div class="download-wrap-bubble">
                            <download-bubble
                                s-if="qrCodeAndroidUrl"
                                class="download-wrap-bubble-android"
                                download-img="{{(qrCodeAndroidUrl}}"
                                title="扫码即刻下载安卓版"
                                show-arrow="{{showArrow}}"
                            ></download-bubble>
                            <download-bubble
                                s-if="qrCodeIosUrl"
                                class="download-wrap-bubble-ios"
                                download-img="{{(qrCodeIosUrl}}"
                                title="扫码即刻下载iOS版"
                                show-arrow="{{showArrow}}"
                            ></download-bubble>
                        </div>
                    </div>
                </div>
                <div
                    id="pc-section-1"
                    class="pc-section pc-section-1"
                    style="background-image: url({{pcBgDark}})"
                    s-ref="section1"
                >
                    <div class="pc-section-main" s-show="isBackgroundLoaded">
                        <div class="pc-section-seat"></div>
                        <div class="text-wrap">
                            {{title}}
                        </div>
                        <div class="subtext-wrap">
                            <p>{{slogan}}</p>
                            <p>{{subTitle}}</p>
                        </div>
                        <div class="btn-wrap">
                            <div class="btn-wrap-android" on-mouseenter="native:handleMouseEnter('android')">
                                <span class="btn-wrap-logo btn-wrap-android-logo"></span>
                                <span>Android</span>
                            </div>
                            <download-bubble
                                class="btn-wrap-bubble btn-wrap-bubble-android"
                                download-img="{{qrCodeAndroidUrl}}"
                                title="手机扫码即刻下载"
                            ></download-bubble>
                            <div class="btn-wrap-ios" on-mouseenter="native:handleMouseEnter('ios')">
                                <span class="btn-wrap-logo btn-wrap-ios-logo"></span>
                                <span>iOS</span>
                            </div>
                            <download-bubble
                                class="btn-wrap-bubble btn-wrap-bubble-ios"
                                download-img="{{qrCodeIosUrl}}"
                                title="手机扫码即刻下载"
                            ></download-bubble>
                        </div>
                        <video-plyr
                            class="video-wrap"
                            poster="{{posterUrl}}"
                            video-url="{{videoUrl}}"
                            on-play="handleVidoe('play')"
                            on-pause="handleVidoe('pause')"
                            on-ended="handleVidoe('ended')"
                            on-enterfullscreen="handleFullScreen"
                        ></video-plyr>
                        <div class="pc-section-seat"></div>
                    </div>
                    <lottie-left-star class="left-star" />
                    <lottie-right-star class="right-star" />
                    <div class="mouse">
                        <lottie-mouse />
                    </div>
                </div>

                <div
                    id="pc-section-2"
                    class="pc-section pc-section-2"
                    style="background-image: url({{pcBgLight}})"
                    s-ref="section2"
                >
                    <div class="pc-section-seat"></div>
                    <div class="pc-section-product">
                        <fragment s-for="item, index in productBgList">
                            <div
                                class="pc-section-img pc-section-img-{{index + 1}}"
                                style="background-image: url({{item}})"
                            >
                                <div class="pc-section-img-mask"></div>
                            </div>
                        </fragment>
                    </div>
                    <page-footer class="pc-section-footer"/>
                    <div class="pc-section-seat"></div>
                </div>
            </div>
        </div>
    `;

    static components = {
        'lottie-mouse': LottieMouse,
        'lottie-left-star': LeftStar,
        'lottie-right-star': RightStar,
        'download-bubble': DownloadBubble,
        'page-footer': Footer,
        'video-plyr': VideoPlyr,
        'cos-button': Button,
        'cos-icon': Icon,
    };

    static computed = {
        showArrow() {
            return !(this.data.get('qrCodeAndroidUrl') && this.data.get('qrCodeIosUrl'));
        },
        isFirstSection() {
            return this.data.get('currentSection') === 1;
        }
    };

    initData() {
        return {
            // logo
            logoWhite: LOGO_URL_WHITE,
            logoBlack: LOGO_URL_BLACK,
            // 安卓-二维码
            qrCodeAndroidUrl: QR_CODE_ANDROID_URL,
            // ios-二维码
            qrCodeIosUrl: QR_CODE_IOS_URL,
            // 下载图标
            downloadIcon: DOWNLOAD_ICON_URL,
            // 深色背景
            pcBgDark: PC_BG_DARK,
            // 浅色背景
            pcBgLight: PC_BG_LIGHT,
            // 视频封面
            posterUrl: POSTER_URL,
            // 视频地址
            videoUrl: VIDEO_URL,
            // 当前section索引
            currentSection: 1,
            // 根文字大小
            rootFontSize: 100,
            // 标题
            title: PAGE_TITLE_1 + '，' + PAGE_TITLE_2,
            // 副标题
            subTitle: PAGE_SUBTITLE,
            // 标语
            slogan: SLOGAN,
            // 监听器数组
            observerArr: [],
            // 产品背景图
            productBgList: [PRODUCT_BG_1, PRODUCT_BG_2, PRODUCT_BG_3, PRODUCT_BG_4],
            // 背景图加载完成
            isBackgroundLoaded: false,
        };
    }
    created(): void {
        gsap.registerPlugin(ScrollTrigger);
        ScrollTrigger.defaults({
            scroller: '.pc-container',
        });
    }

    attached(): void {
        this.cardAnimate();
        this.backgroundAnimate();
        this.handleSection();
        this.nextTick(() => {
            this.backgroundImageLoaded();
        });
    }

    // 全屏
    handleFullScreen() {
        this.fire('enterfullscreen');
    }

    // 图片加载完成
    backgroundImageLoaded() {
        const bg = new Image() as HTMLImageElement;
        bg.src = this.data.get('pcBgDark');
        
        bg.onload = () => {
            // 图片加载完成，提升体验再延迟200ms
            setTimeout(() => {
                this.data.set('isBackgroundLoaded', true);
            }, 150);
        };
        
        bg.onerror = () => {
            setTimeout(() => {
                this.data.set('isBackgroundLoaded', true);
            }, 150);
        };
    }

    detached(): void {
        this.destroyObserver();
    }

    // 销毁监听器
    destroyObserver() {
        const observers = this.data.get('observerArr') || [];
        observers.forEach((observer: IntersectionObserver) => {
            observer && observer.disconnect();
        });
        this.data.set('observerArr', []);
    }

    // section切换
    handleSection() {
        const sectionThreshold = [
            {
                threshold: 0.01,
                section: 1,
            },
            {
                threshold: 0.99,
                section: 2,
            },
        ];
        const observerArr: IntersectionObserver[] = [];

        sectionThreshold.forEach(item => {
            const el = this.ref(`section${item.section}`);
            if (el instanceof HTMLElement) {
                const observer = handleObserver({
                    el,
                    callback: () => {
                        this.data.set('currentSection', item.section);
                    },
                    threshold: item.threshold,
                }) as IntersectionObserver;
                observerArr.push(observer);
            }
        });
        this.data.set('observerArr', observerArr);
    }

    /**
     * 背景动画
     */
    backgroundAnimate() {
        const rootFontSize = this.data.get('rootFontSize');
        // 第一屏到第二屏背景动画
        ScrollTrigger.create({
            trigger: '.pc-section-2',
            start: 'top center',
            end: `top ${rootFontSize}px`,
            scrub: 5,
        });
    }

    /**
     * 卡片展现隐藏动画
     */
    cardAnimate() {
        const rootFontSize = this.data.get('rootFontSize');
        ScrollTrigger.create({
            trigger: '.pc-section-2',
            start: `top-=${rootFontSize * 3.8}px`,
            end: `top-=${rootFontSize * 1.8}px`,
            scrub: 2,
            animation: gsap.from(
                '.pc-section-img-1',
                {y: 100, opacity: 0, ease: 'slow(0.7, 0.7, false)', duration: 10}
            ),
        });

        ScrollTrigger.create({
            trigger: '.pc-section-2',
            start: `top-=${rootFontSize * 3.2}px`,
            end: `top-=${rootFontSize * 1.2}px`,
            scrub: 2,
            animation: gsap.from(
                '.pc-section-img-2', {y: 100, opacity: 0, ease: 'slow(0.7, 0.7, false)', duration: 10}
            ),
        });

        ScrollTrigger.create({
            trigger: '.pc-section-2',
            start: `top-=${rootFontSize * 2.6}px`,
            end: `top-=${rootFontSize * 0.6}px`,
            scrub: 2,
            animation: gsap.from(
                '.pc-section-img-3', {y: 100, opacity: 0, ease: 'slow(0.7, 0.7, false)', duration: 10}
            ),
        });

        ScrollTrigger.create({
            trigger: '.pc-section-2',
            start: `top-=${rootFontSize * 2}px`,
            end: 'top',
            scrub: 2,
            animation: gsap.from(
                '.pc-section-img-4', {y: 100, opacity: 0, ease: 'slow(0.7, 0.7, false)', duration: 10}
            ),
        });
    }

    // 播放/暂停/结束
    handleVidoe(action: string) {
        handleLog({
            category: 'video',
            action,
            label: 'homepage',
            value: 1,
        });
    }

    // 二维码展现
    handleMouseEnter(type?: string) {
        handleLog({
            category: 'qcode',
            action: 'show',
            label: 'homepage',
            value: type === 'android' ? 1 : type === 'ios' ? 2 : 3,
        });
    }
}